import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/layout/Layout';
import { TripTable } from '../components/trips/TripTable';
import { TripFilters } from '../components/trips/TripFilters';
import { TripDetailModal } from '../components/trips/TripDetailModal';
import { ExportButtons } from '../components/reports/ExportButtons';
import { GenerateDemoTripsModal } from '../components/trips/GenerateDemoTripsModal';
import { useTrips } from '../hooks/useTrips';
import { useAuth } from '../hooks/useAuth';
import { Trip, TripFilters as TripFiltersType } from '../types/trip';
import { tripsApi } from '../api/trips.api';

export const TripsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin === true || user?.role === 'super_admin';
  const isCompanyAdmin = user?.role === 'company_admin';

  // GPS visibility: super_admin always sees GPS. Driver always sees GPS.
  // company_admin and all other roles depend on the company toggle.
  const canViewGps =
    isSuperAdmin ||
    user?.role === 'driver' ||
    (user?.company?.gps_data_visible_to_company_users === true);

  const [showDemoModal, setShowDemoModal] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);

  const [filters, setFilters] = useState<TripFiltersType>({
    startDate: null,
    endDate: null,
    driverId: null,
    qualification: null,
    search: '',
    page: 1,
    limit: 20,
  });

  const { data: trips, isLoading, pagination, refetch } = useTrips(filters);

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage });
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-900">{t('trips.title')}</h1>
          <div className="flex flex-wrap items-center gap-3">
            {isSuperAdmin && (
              <button
                onClick={() => setShowDemoModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                title="Super Admin: Generate demo trips for testing"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                  />
                </svg>
                {t('trips.generateDemo')}
              </button>
            )}
            <ExportButtons
              filters={{
                startDate: filters.startDate || undefined,
                endDate: filters.endDate || undefined,
                driverId: filters.driverId || undefined,
              }}
            />
          </div>
        </div>

        {/* Filters */}
        <TripFilters filters={filters} onChange={setFilters} />

        {/* Results Count */}
        {!isLoading && (
          <div className="text-sm text-gray-700">
            <span className="font-medium">{pagination.total}</span>{' '}
            {t('trips.found', { count: pagination.total }).replace(/^\d+ /, '')}
          </div>
        )}

        {/* Trip Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <TripTable
            trips={trips}
            isLoading={isLoading}
            onRefresh={refetch}
            onView={(trip) => setSelectedTrip(trip)}
          />

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-sm text-gray-700">
                {t('trips.showing', {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(pagination.page * pagination.limit, pagination.total),
                  total: pagination.total,
                })}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('trips.previous')}
                </button>
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          pagination.page === page
                            ? 'bg-primary-500 text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  {pagination.totalPages > 5 && <span className="px-2">...</span>}
                  {pagination.totalPages > 5 && (
                    <button
                      onClick={() => handlePageChange(pagination.totalPages)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        pagination.page === pagination.totalPages
                          ? 'bg-primary-500 text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {pagination.totalPages}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasMore}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('trips.next')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showDemoModal && (
        <GenerateDemoTripsModal
          onClose={() => setShowDemoModal(false)}
          onSuccess={() => {
            setShowDemoModal(false);
            refetch();
          }}
        />
      )}

      {selectedTrip && (
        <TripDetailModal
          trip={selectedTrip}
          canViewGps={canViewGps}
          onClose={() => setSelectedTrip(null)}
          onQualify={async (tripId, qualification) => {
            await tripsApi.qualifyTrip(tripId, qualification);
            refetch();
          }}
        />
      )}
    </Layout>
  );
};
